/**
 * SuperAdminForceDelete — Iteration 96.
 *
 * Notfall-Werkzeug für Super-Admins: löscht signierte Angebote (die
 * normalerweise durch den Signatur-Guard geschützt sind) sowie verwaiste
 * Projekte und optional Kundendatensätze. Jede Aktion wird ins audit_log
 * geschrieben.
 *
 * Zwei-Stufen-Bestätigung + immer erst Dry-Run.
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import {
  Trash2, AlertTriangle, Loader2, ChevronRight, CheckCircle2, ShieldAlert,
} from "lucide-react";

// Iter96 vorausgefüllt — die 4 Test-Projekte, die der Kunde in seinem
// Screenshot markiert hat. Nach dem ersten Delete-Lauf kann er das Feld
// wieder leeren und neue Nummern hinzufügen.
const DEFAULT_NUMBERS = [
  "260676-0004",
  "260676-0002",
  "260576-0001",
  "260514-0001",
];

export default function SuperAdminForceDelete() {
  const [numbers, setNumbers] = useState(DEFAULT_NUMBERS.join("\n"));
  const [deleteOrphanProjects, setDop] = useState(true);
  const [deleteOrphanCustomers, setDoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const parseNumbers = () =>
    numbers.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

  const run = async (dry_run) => {
    const offer_numbers = parseNumbers();
    if (offer_numbers.length === 0) {
      toast.error("Keine Angebotsnummern angegeben");
      return;
    }
    if (!dry_run && !window.confirm(
      `WIRKLICH ${offer_numbers.length} Angebote endgültig löschen? ` +
      `Diese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/superadmin/danger/force-delete-offers", {
        offer_numbers,
        delete_orphan_projects: deleteOrphanProjects,
        delete_orphan_customers: deleteOrphanCustomers,
        dry_run,
      });
      setPreview(r.data);
      if (!dry_run) {
        toast.success(`${r.data.results.filter(x => x.found).length} Angebote gelöscht`);
        setConfirmed(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AppHeader />
      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-red-500/15 border border-red-500/40">
            <ShieldAlert size={22} className="text-red-300" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-100">Danger Zone — Force Delete</h1>
            <p className="text-[13px] text-zinc-300 font-medium">
              Signierte Angebote + verwaiste Projekte manuell entfernen (nur für Testdaten)
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4 mb-4 text-[13px] text-red-200 leading-relaxed">
          <div className="flex gap-2">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              Diese Aktion umgeht den Signatur-Schutz und ist <b>nicht rückgängig zu machen</b>.
              Alle Löschungen werden im <span className="font-mono">audit_log</span> mit Deiner
              Super-Admin-Kennung protokolliert. Bitte <b>immer erst „Vorschau"</b> klicken.
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-[#18181B] p-4 space-y-4">
          <div>
            <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5 block">
              Angebotsnummern (eine pro Zeile)
            </label>
            <textarea
              value={numbers}
              onChange={(e) => setNumbers(e.target.value)}
              rows={5}
              data-testid="danger-numbers"
              className="w-full font-mono text-[13.5px] bg-[#0F1418] border border-zinc-800 rounded-xl p-3 text-zinc-100 focus:border-red-500/50 focus:outline-none"
              placeholder="260676-0004"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              {parseNumbers().length} Angebotsnummer(n) erkannt
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[13px] text-zinc-200 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteOrphanProjects}
                onChange={(e) => setDop(e.target.checked)}
                data-testid="danger-orphan-projects"
                className="rounded"
              />
              Verwaiste Projekte mitlöschen (empfohlen)
            </label>
            <label className="flex items-center gap-2 text-[13px] text-zinc-200 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteOrphanCustomers}
                onChange={(e) => setDoc(e.target.checked)}
                data-testid="danger-orphan-customers"
                className="rounded"
              />
              Verwaiste Kundendatensätze mitlöschen (Vorsicht — meistens NICHT gewünscht)
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => run(true)}
              disabled={busy}
              data-testid="danger-preview"
              className="flex-1 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <ChevronRight size={16} />}
              Vorschau (Dry-Run)
            </button>
            <button
              onClick={() => run(false)}
              disabled={busy || !confirmed}
              data-testid="danger-commit"
              className="flex-1 h-11 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/60 font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 size={16} /> ENDGÜLTIG LÖSCHEN
            </button>
          </div>

          {preview && (
            <label className="flex items-center gap-2 text-[12.5px] text-zinc-300 cursor-pointer border-t border-zinc-800 pt-3">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="danger-confirm"
              />
              Ich habe die Vorschau geprüft und bestätige die endgültige Löschung.
            </label>
          )}
        </div>

        {preview && (
          <div className="mt-5 rounded-2xl border border-zinc-800 bg-[#18181B] overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2 bg-zinc-900/50">
              {preview.dry_run
                ? <><ChevronRight size={16} className="text-amber-300" /> <b className="text-amber-300">Vorschau</b></>
                : <><CheckCircle2 size={16} className="text-[#00E575]" /> <b className="text-[#00E575]">Ausgeführt</b></>}
              <span className="ml-auto text-[11px] text-zinc-400 font-mono">Actor: {preview.actor}</span>
            </div>
            <div className="divide-y divide-zinc-800">
              {preview.results.map((r) => (
                <div key={r.offer_number} className="p-3.5" data-testid={`danger-result-${r.offer_number}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-mono text-[13.5px] font-bold text-zinc-100">{r.offer_number}</div>
                    <div className="flex items-center gap-2">
                      {r.found ? (
                        <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase ${r.status === "signed" ? "bg-[#00E575]/15 text-[#00E575]" : "bg-zinc-800 text-zinc-300"}`}>
                          {r.status || "—"}
                        </span>
                      ) : (
                        <span className="text-red-300 text-[12px] font-bold">Nicht gefunden</span>
                      )}
                    </div>
                  </div>
                  {r.found && (
                    <div className="mt-1.5 text-[12px] text-zinc-400 space-y-0.5">
                      <div>Projekt: <span className="font-mono text-zinc-300">{r.project_id}</span> · Weitere Angebote: <b className="text-zinc-200">{r.sibling_offers}</b></div>
                      {r.signer && <div>Unterzeichnet von: <span className="text-zinc-200">{r.signer}</span></div>}
                      {r.actions?.length > 0 && (
                        <ul className="mt-1 pl-3 space-y-0.5">
                          {r.actions.map((a, i) => (
                            <li key={i} className="font-mono text-[11px] text-zinc-400">• {a}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
